import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import TimesheetService from "../TimesheetService";
import {
    DKLabel,
    DKButton,
    DKInput,
    DKCheckMark,
    DKTooltipWrapper,
    DKIcon,
    DKIcons,
    DKSpinner,
    Toggle,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE
} from "deskera-ui-library";
import { fetchEmployeeListWithoutFilter, selectEmployeeListWithoutLimit } from "../../../Redux/Slices/EmployeeListSlice";
import { selectBooksContact } from "../../../Redux/Slices/BooksContactSlice";
import { selectProject } from "../../../Redux/Slices/ProjectSlice";
import Utility from "../../../utility/Utility";

function AddEmployeeMapping(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const employeeResponse = useSelector(selectEmployeeListWithoutLimit);
    const booksContact = useSelector(selectBooksContact);
    const project = useSelector(selectProject);
    const initialFormState = {
        employeeId: {
            id: "employeeId",
            title: t("Employee"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]]
        },
        customer: {
            id: "customer",
            title: t("Customer"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]]
        },
        projectId: {
            id: "projectId",
            title: t("Project"),
            type: INPUT_TYPE.DROPDOWN,
            validation: [["required"]],
            readOnly: true
        },
        ratePerHour: {
            id: "ratePerHour",
            title: t("Rate Per Hour"),
            type: INPUT_TYPE.NUMBER,
            validation: [["required"]]
        }
    }

    const [formState, setFormState] = useState(initialFormState);
    const [mapping, setMapping] = useState(null);
    const [fetchingDependencies, setFetchingDependencies] = useState(true);
    const [updateContact, setUpdateContact] = useState(true)
    useEffect(() => {
        if (TimesheetService.EMPLOYEE_MAPPING) {
            setMapping(TimesheetService.EMPLOYEE_MAPPING);
            TimesheetService.EMPLOYEE_MAPPING = null;
        }

        fetchDependencies();
    }, []);

    useEffect(() => {
        if (!fetchingDependencies) {
            const newState = { ...formState };

            newState.customer.options = booksContact?.map(e=>{ return {...e,label:e.name,value: e.id?.toString()}});

            if (employeeResponse) {
                newState.employeeId.options = employeeResponse.users.map((user) => {
                    return {
                        value: user.userId,
                        label: user.name
                    }
                });
            }

            newState.projectId.options = project.data.map(e=>{ return {...e,label:e.name,value: e.id?.toString()}});
            if (mapping) {
                setProjectData(newState);
            }

            setFormState(newState);
        }
    }, [fetchingDependencies, mapping, employeeResponse]);

    const fetchDependencies = async () => {
        await dispatch(fetchEmployeeListWithoutFilter());
        setFetchingDependencies(false);
    }

    const setProjectData = (newState) => {
        newState.employeeId.value = getDropdownLabel(mapping.employeeId, newState.employeeId.options);
        newState.customer.value = getDropdownLabel(mapping.customer, newState.customer.options);
        newState.projectId.value = getDropdownLabel(mapping.projectId, newState.projectId.options);
        newState.ratePerHour.value = mapping.ratePerHour;
    }

    const getDropdownLabel = (v, o) => {
        let value = v;

        const index = o?.findIndex(i => i.value === v);
        if (index !== -1 && !Utility.isEmpty(o)) {
            value = o[index]?.label;
        }

        return value;
    }

    const getDropdownValue = (v, o) => {
        let value = v;

        const index = o.findIndex(i => i.label === v);
        if (index !== -1) {
            value = o[index].value;
        }

        return value;
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }

        return error;
    }

    const updateState = (stateName, field, value) => {
        const newState = { ...formState };
        newState[stateName] = { ...field, value: value, ...hasError(field, value) };
        updateValueAndValidity(stateName, newState);

        setFormState(newState);
    }

    const updateValueAndValidity = (stateName, state) => {
        switch (stateName) {
            case "customer":
                const customerId = getDropdownValue(state.customer.value, state.customer.options);
                const projectOption = project?.data?.map(e=>{ return {...e,label:e.name,value: e.id}})
                state.projectId.options = projectOption.filter((p) => p.customer === customerId).map((p) => {
                        return {
                            value: p.id,
                            label: p.name
                        }
                    });
                state.projectId.readOnly = false;
                state.projectId.value = null;
                break;
            default:
                break;
        }
    }

    const isInvalid = () => {
        let invalid = false;

        const newState = { ...formState };
        Object.entries(newState).forEach(([key, field]) => {
            const error = hasError(field, field.value);
            field.invalid = error.invalid;
            field.errorMessage = error.errorMessage;

            if (field.invalid) {
                invalid = true;
            }
        });

        setFormState(newState);

        return invalid;
    }

    const getPayload = () => {
        const payload = {
            employeeId: getDropdownValue(formState.employeeId.value, formState.employeeId.options),
            customer: getDropdownValue(formState.customer.value, formState.customer.options),
            projectId: getDropdownValue(formState.projectId.value, formState.projectId.options),
            ratePerHour: formState.ratePerHour.value
        };

        if (mapping) {
            payload.id = mapping.id;
        }

        return payload;
    }

    const saveEmployeeMapping = () => {
        if (isInvalid()) {
            return;
        }
        const payload = getPayload();
        showLoader();
        if (mapping) {
            TimesheetService.updateEmployeeMapping(payload).then(res=>{
                showToast("Employee mapping updated successfully.", TOAST_TYPE.SUCCESS)
                removeLoader();
                props.onClose(true);
            },error=>{
                removeLoader()
                const message = error && error.message ? error.message : t("SOMETHING_WENT_WRONG")
                showToast(message, TOAST_TYPE.FAILURE);
            })
        } else {
            TimesheetService.saveEmployeeMapping(payload).then(res=>{
                showToast("Employee mapping added successfully.", TOAST_TYPE.SUCCESS)
                removeLoader();
                props.onClose(true);
            },error=>{
                removeLoader()
                const message = error && error.message ? error.message : t("SOMETHING_WENT_WRONG")
                showToast(message, TOAST_TYPE.FAILURE);
            })
        }
    }

    const renderField = (stateName, field, width, className) => {
        switch (field.type) {
            case INPUT_TYPE.TEXT:
            case INPUT_TYPE.NUMBER:
            case INPUT_TYPE.DATE:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                ref={field.ref}
                                title={field.title}
                                type={field.type}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                                validator={field.validator}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.DROPDOWN:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {
                                        updateState(stateName, field, value.label);
                                    }
                                }}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                onChange={() => { }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.MULTI_SELECT:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className={`row position-relative ${!className ? "pb-l mb-s" : className} ${field.readOnly && "opacity-5"}`}>
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    multiSelect: true,
                                    checkMarkColor: "bg-app",
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => { },
                                }}
                                displayKey={"label"}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly || fetchingDependencies}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                tooltip={{
                                    content: field.tooltip,
                                    className: "popup-tooltip-prop"
                                }}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                            />
                        </div>
                    </div>
                );
            case INPUT_TYPE.CHECKBOX:
                return (
                    <div key={field.id} style={{ flex: `${width}`, maxWidth: width }}>
                        <div className="row position-relative pb-l mb-s">
                            <div className="d-flex dkinput-header-section mr-l">
                                <DKLabel className="mb-xs" text={`${field.title} ${isRequired(field.validation) ? "*" : ""}`} />
                                {field.tooltip &&
                                    <DKTooltipWrapper content={field.tooltip} tooltipClassName="checkbox-tooltip-prop">
                                        <DKIcon src={DKIcons.ic_info} className="ic-s ml-s cursor-hand" style={{ opacity: 0.4 }} />
                                    </DKTooltipWrapper>
                                }
                            </div>
                            <DKCheckMark
                                isSelected={field.value}
                                color="bg-app"
                                onClick={() => {
                                    updateState(stateName, field, !field.value);
                                }}
                            />
                        </div>
                    </div>
                );
            case "toggle":
                return (
                    <div key={field.id} style={{ flex: "0 0 100%", maxWidth: width }}>
                        <div className="row pb-l mb-s position-relative">
                            <DKLabel style={{ width: "46%" }} className="mb-xs" text={isRequired(field.validation) ? `${field.title} *` : field.title} />
                            <Toggle
                                style={{ width: "20%" }}
                                isOn={field.value}
                                onChange={() => {
                                    updateState(stateName, field, !field.value);
                                }}
                            />
                        </div>
                    </div>
                );
            default:
                break;
        }
    }

    return (
        <div className="transparent-background">
            <div className="popup-window" style={{ padding: 0, overflow: "hidden" }}>
                <div className="parent-width" style={{ height: "50vh" }}>
                    <div className="row p-h-xl pt-xl pb-m">
                        <DKLabel text={mapping ? t("Edit Mapping") : t("Add Mapping")} className="fs-l fw-b" />
                        {fetchingDependencies && <DKSpinner className="pl-s" iconClassName="ic-s-2" />}
                        <div className="ml-auto d-flex">
                            <DKButton
                                title={t("CANCEL")}
                                className="border-radius-m bg-gray1 border-m"
                                onClick={() => {
                                    props.onClose();
                                }}
                            />
                            <DKButton
                                title={t("SAVE")}
                                className="bg-app text-white ml-m"
                                onClick={() => {
                                    saveEmployeeMapping();
                                }}
                            />
                        </div>
                    </div>
                    <div className="parent-width custom-scroll" style={{ overflow: "auto", height: "calc(100% - 69px)" }}>
                        <div className="p-h-xl p-v-m">
                            {renderField("employeeId", formState.employeeId, "100%")}
                            {renderField("customer", formState.customer, "100%")}
                            {renderField("projectId", formState.projectId, "100%")}
                            {renderField("ratePerHour", formState.ratePerHour, "100%")}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddEmployeeMapping;
